import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { Bell } from 'lucide-react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// A bell with an unread badge and a dropdown list. The data source is passed in, so Project
// Central (task notifications) and Marketing Email (campaign notifications) share one UI.
const NotificationBell = ({
  queryKey,
  fetchUnreadCount, // () => Promise<number>
  fetchList, // () => Promise<item[]>
  isUnread, // (item) => boolean
  onSelect, // async (item) => void — mark read, navigate…
  onMarkAllRead, // () => Promise
  renderItem, // (item) => node; defaults to title + message
  title = 'Notifications',
  label = 'Notifications',
  icon: Icon = Bell,
  emptyText = 'No notifications yet',
  footer // ({ close }) => node
}) => {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()

  // Poll, and also check when the tab regains focus (the app turns that off globally)
  const { data: unreadCount = 0 } = useQuery([queryKey, 'unread-count'], fetchUnreadCount, { refetchInterval: 30000, refetchOnWindowFocus: true })
  const { data: items = [], isLoading } = useQuery([queryKey, 'list'], fetchList, { enabled: open })

  const refresh = () => queryClient.invalidateQueries(queryKey)
  const close = () => setOpen(false)

  const handleSelect = async (item) => {
    close()
    await onSelect(item)
    refresh()
  }

  const handleMarkAllRead = async () => {
    await onMarkAllRead()
    refresh()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={unreadCount ? `${label} (${unreadCount} unread)` : label} title={label}>
          <Icon className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 max-w-[calc(100vw-2rem)] p-0" align="end">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-medium">{title}</span>
          {unreadCount > 0 && (
            <button type="button" className="text-xs text-primary hover:underline" onClick={handleMarkAllRead}>
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[28rem] overflow-y-auto">
          {isLoading && <p className="px-3 py-4 text-sm text-muted-foreground">Loading…</p>}
          {!isLoading && items.length === 0 && <p className="px-3 py-4 text-sm text-muted-foreground">{emptyText}</p>}
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelect(item)}
              className={cn(
                'w-full text-left px-3 py-2.5 border-b last:border-b-0 hover:bg-accent transition-colors',
                isUnread(item) && 'bg-accent/40'
              )}
            >
              {renderItem ? renderItem(item) : (
                <>
                  <p className="text-sm font-medium">{item.title}</p>
                  {item.message && <p className="text-xs text-muted-foreground mt-0.5">{item.message}</p>}
                </>
              )}
            </button>
          ))}
        </div>
        {footer && <div className="border-t px-3 py-2">{footer({ close })}</div>}
      </PopoverContent>
    </Popover>
  )
}

export default NotificationBell
